const CACHE_LIMIT = 20;
const DEFAULT_CONFIG = {
  routes: { searchUrl: '/search', predictiveSearchUrl: '/search/suggest' },
  hiddenCollectionHandles: [],
  brands: []
};

const cache = new Map();

const normalize = (value = '') => String(value)
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/đ/g, 'd').replace(/Đ/g, 'd')
  .toLowerCase().trim();

const compactCode = (value = '') => normalize(value).replace(/\s+/g, '');
const hasSpacedDigits = (value = '') => /\d+\s+\d/.test(String(value));
const looksLikeCode = (value = '') => /\d/.test(String(value));

const configFromDocument = () => {
  if (typeof document === 'undefined') return DEFAULT_CONFIG;
  const node = document.querySelector('[data-search-config]');
  if (!node) return DEFAULT_CONFIG;
  try { return { ...DEFAULT_CONFIG, ...JSON.parse(node.textContent || '{}') }; }
  catch { return DEFAULT_CONFIG; }
};

const urlWith = (base, params) => {
  const url = new URL(base, typeof window === 'undefined' ? 'https://example.test' : window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, value);
  });
  return url.pathname + url.search;
};

const readPredictive = async (query, config, signal) => {
  const url = urlWith(`${config.routes.predictiveSearchUrl}.json`, {
    q: query,
    'resources[type]': 'query,collection,article',
    'resources[limit]': 6,
    'resources[limit_scope]': 'each'
  });
  const response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
  const body = await response.json();
  const resources = body.resources?.results || {};
  const hidden = new Set(config.hiddenCollectionHandles || []);
  const brandUrls = new Set((config.brands || []).map((brand) => brand.url).filter(Boolean));
  return {
    keywords: (resources.queries || []).map((item) => ({
      title: item.text || item.styled_text || item.title || String(item),
      url: item.url || ''
    })).filter((item) => item.title).slice(0, 6),
    collections: (resources.collections || []).filter((item) => {
      const handle = item.handle || String(item.url || '').split('/').pop();
      return !hidden.has(handle) && !brandUrls.has(item.url);
    }).map((item) => ({
      title: item.title,
      url: item.url,
      handle: item.handle || String(item.url || '').split('/').pop(),
      image: item.image || item.featured_image?.url || ''
    })).slice(0, 6),
    articles: (resources.articles || []).map((item) => ({
      title: item.title,
      url: item.url,
      type: item.blog?.title || 'Bài viết'
    })).slice(0, 6)
  };
};

const readProducts = async (query, config, signal) => {
  const url = urlWith(config.routes.searchUrl, {
    q: query,
    type: 'product',
    'options[prefix]': 'last',
    view: 'suggest'
  });
  const response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Product search failed: ${response.status}`);
  return response.json();
};

const mergeProducts = (lists) => {
  const products = [];
  const seen = new Set();
  lists.flatMap((item) => item.products || []).forEach((product) => {
    const key = String(product.id || product.url || product.title);
    if (seen.has(key)) return;
    seen.add(key);
    products.push(product);
  });
  return products;
};

const titleCodeTokens = (title = '') => {
  const words = normalize(title).replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean);
  const tokens = [];
  words.forEach((word) => {
    if (/\d/.test(word)) tokens.push(word);
  });
  for (let start = 0; start < words.length; start += 1) {
    if (!/\d/.test(words[start])) continue;
    let phrase = words[start];
    for (let end = start + 1; end < words.length && /\d/.test(words[end]); end += 1) {
      phrase += words[end];
      tokens.push(phrase);
    }
  }
  return tokens;
};

const skuHitFor = (query, products) => {
  if (!looksLikeCode(query)) return null;
  const target = compactCode(query);
  if (!target) return null;
  return products.find((product) => {
    const sku = compactCode(product.sku);
    return sku === target || titleCodeTokens(product.title).some((token) => compactCode(token) === target);
  }) || null;
};

const brandMatches = (query, config, products) => {
  const q = normalize(query);
  if (!q) return [];
  const vendorSet = new Set(products.map((product) => normalize(product.vendor)).filter(Boolean));
  return (config.brands || []).filter((brand) => {
    const name = normalize(brand.name);
    return name.includes(q) || q.includes(name) || vendorSet.has(name);
  }).slice(0, 4);
};

const cacheSet = (key, value) => {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, value);
  while (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
};

export const suggest = async (query, { scope = '', signal, config } = {}) => {
  const cleanQuery = String(query || '').trim();
  const activeConfig = config || configFromDocument();
  const key = JSON.stringify([cleanQuery, scope]);
  if (!cleanQuery) {
    return {
      keywords: [], collections: [], articles: [], products: [],
      brands: [], skuHit: null, total: 0, requestCount: 0
    };
  }
  if (cache.has(key)) return { ...cache.get(key), cached: true, requestCount: 0 };

  const productRequests = [readProducts(cleanQuery, activeConfig, signal)];
  if (hasSpacedDigits(cleanQuery)) productRequests.push(readProducts(`"${cleanQuery}"`, activeConfig, signal));
  const [predictive, ...productBodies] = await Promise.all([
    readPredictive(cleanQuery, activeConfig, signal),
    ...productRequests
  ]);
  const products = mergeProducts(productBodies);
  const hit = skuHitFor(cleanQuery, products);
  if (hit) {
    products.sort((a, b) => (a.id === hit.id ? -1 : b.id === hit.id ? 1 : 0));
  }
  const result = {
    ...predictive,
    brands: brandMatches(cleanQuery, activeConfig, products),
    products,
    skuHit: hit,
    total: Math.max(...productBodies.map((body) => Number(body.total || 0)), products.length),
    requestCount: 1 + productRequests.length
  };
  cacheSet(key, result);
  return result;
};

export const clearSearchSourceCache = () => cache.clear();
export const normalizeSearchText = normalize;
export const compactSearchCode = compactCode;
export const titleSearchCodeTokens = titleCodeTokens;
